import { Component, OnInit, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { DateEx } from '../../functions/date-ex';
import { Alert } from '../../models/api/alerts/alert';
import { AlertsDataService } from '../../services/alerts/alerts-data.service';
import { BaseComponent } from '../base/base.component';
import { ButtonComponent } from '../core/buttons/button.component';
import { ToolbarButtonComponent } from '../core/buttons/toolbar-button.component';
import { DotLabelComponent } from '../core/labels/dot-label.component';
import { TagComponent } from '../core/labels/tag.component';
import { ActionRowComponent } from '../core/panels/action-row.component';
import { LoadingPanelComponent } from '../core/panels/loading-panel.component';
import { ColumnComponent } from '../core/table/column.component';
import { TableComponent } from '../core/table/table.component';
import { HomeCardComponent } from './home-card.component';

@Component({
  selector: 'app-alerts-card',
  imports: [
    HomeCardComponent,
    TableComponent,
    ColumnComponent,
    TagComponent,
    ButtonComponent,
    ActionRowComponent,
    RouterLink,
    DotLabelComponent,
    LoadingPanelComponent,
    ToolbarButtonComponent,
  ],
  template: `
    <app-home-card
      type="Large"
      [padding]="false"
      [minHeight]="'410px'"
      classes="md:tw-h-[420px]"
    >
      <div class="tw-flex tw-justify-between tw-items-center">
        <div>
          <div class="my-alerts">
            <h4>Alerts</h4>
          </div>
        </div>
        <app-toolbar-button
          icon="fa-light fa-arrows-rotate"
          ariaLabel="Refresh alerts"
          (click)="loadAlerts(true)"
        />
      </div>

      <div class="tw-h-[260px] tw-pt-8">
        <app-loading-panel
          [state]="loading ? 'Loading' : 'Loaded'"
          loadingStyle="Box"
          [loadingHeight]="220"
          [error]="error"
        >
          <ng-template #loaded>
            @if (myAlerts.length === 0) {
              <div class="tw-pb-4">
                <div class="tw-flex tw-flex-col tw-pt-4 tw-h-[240px]">
                  <img
                    src="assets/images/home/check.webp"
                    width="60px"
                    height="60px"
                    alt="Check"
                  />
                  <div class="tw-text-[32px]">
                    <div class="tw-line-clamp-2">
                      You're all caught up! No alerts at this time.
                    </div>
                  </div>
                </div>
              </div>
            } @else {
              <div class="tw-max-h-[220px] tw-overflow-y-auto">
                <app-table
                  [dataSource]="myAlerts"
                  [pageSize]="999"
                  [scrollable]="false"
                  [showFilter]="false"
                  [showRecordLabel]="false"
                  [showPaginator]="false"
                  [showToolbar]="false"
                  [showColumnHeaders]="false"
                  theme="Light"
                >
                  <app-column name="name">
                    <ng-template let-data>
                      <div
                        class="tw-flex tw-flex-row md:tw-items-start tw-gap-2 tw-w-full tw-min-w-0 tw-whitespace-normal"
                      >
                        <div
                          class="tw-font-bold tw-text-[14px] tw-flex-1 tw-min-w-0 tw-break-words"
                        >
                          {{ data.name }}
                        </div>

                        <div
                          class="tw-shrink-0 tw-hidden md:tw-block md:tw-ml-auto"
                        >
                          <app-tag [type]="data.active ? 'Success' : 'Error'">{{
                            data.active ? 'Active' : 'Inactive'
                          }}</app-tag>
                        </div>
                      </div>
                    </ng-template>
                  </app-column>

                  <app-column
                    name="newMessages"
                    classes="tw-align-bottom md:tw-align-middle"
                  >
                    <ng-template let-data>
                      <div
                        class="tw-flex tw-justify-between md:tw-justify-start tw-items-center tw-w-full"
                      >
                        <div class="md:tw-hidden">
                          <app-tag [type]="data.active ? 'Success' : 'Error'">{{
                            data.active ? 'Active' : 'Inactive'
                          }}</app-tag>
                        </div>
                        <app-dot-label
                          [label]="getNewMessagesLabel(data.newMessages)"
                          [color]="getNewMessagesColor(data.newMessages)"
                        />
                      </div>
                    </ng-template>
                  </app-column>
                </app-table>
              </div>
            }
          </ng-template>
        </app-loading-panel>
      </div>

      <app-action-row class="tw-pt-4 tw-px-4">
        <app-button
          left1
          label="View alerts"
          [routerLink]="['/alerts']"
          [queryParams]="{ tab: 'messages' }"
        />
      </app-action-row>
    </app-home-card>
  `,
  styles: `
    .my-alerts {
      padding-right: 0.5rem;
      padding-left: 0.5rem;
      font-weight: bold;
      background-color: var(--sed-sedgwick-green-color);
      width: 80px;
      border-radius: 8px;
      text-align: center;
    }
  `,
})
export class AlertsCardComponent extends BaseComponent implements OnInit {
  private alertsDataService = inject(AlertsDataService);

  myAlerts: AlertDisplay[] = [];
  loading = true;
  error: any = null;

  async ngOnInit(): Promise<void> {
    await this.loadAlerts();
    this.dispose.subs = this.alertsDataService.messagesChanged.subscribe(
      async (_) => {
        await this.loadAlerts();
      },
    );
    this.dispose.subs = this.alertsDataService.alertsChanged.subscribe(
      async (_) => {
        await this.loadAlerts();
      },
    );
  }

  protected async loadAlerts(refreshCache = false): Promise<void> {
    this.error = null;
    this.loading = true;
    try {
      // Fetch alerts and message counts in parallel
      const [alerts, messages] = await Promise.all([
        this.alertsDataService.getAlerts(refreshCache),
        this.alertsDataService.getMessages(refreshCache),
      ]);

      // Create a map for quick lookup of message counts
      const countsMap = new Map<
        number,
        { newMessages: number; totalMessages: number }
      >();
      for (const message of messages) {
        if (!countsMap.has(message.alertId)) {
          countsMap.set(message.alertId, {
            newMessages: 0,
            totalMessages: 0,
          });
        }

        const counter = countsMap.get(message.alertId);
        counter.totalMessages++;
        if (message.messageStatus === 'NEW') {
          counter.newMessages++;
        }
      }

      // Map alerts to display format, only show alerts with new messages first
      this.myAlerts = alerts
        .map((alert) => {
          const counts = countsMap.get(alert.alertId);

          return {
            alertId: alert.alertId,
            name: alert.description ?? 'Unnamed Alert',
            active: this.isAlertActive(alert),
            messages: counts?.totalMessages ?? 0,
            newMessages: counts?.newMessages ?? 0,
          };
        })
        .filter((a) => a.newMessages > 0) // Show alerts with unread messages only
        .sort((a, b) => a.name.localeCompare(b.name)); // Sort alphabetically by description (Req 3.2.002)
    } catch (error) {
      this.error = error;
    } finally {
      this.loading = false;
    }
  }

  getNewMessagesLabel(newMessages: number): string {
    return newMessages === 0
      ? `${newMessages} unread messages`
      : newMessages === 1
        ? `${newMessages} unread message`
        : `${newMessages} unread messages`;
  }

  getNewMessagesColor(newMessages: number): string {
    return newMessages > 0
      ? 'var(--sed-sedgwick-green-color)'
      : 'var(--sed-grey-100-color)';
  }

  /** Matches Manage Alerts / classic VOV5 IsAlertInactive (date-only, inclusive). */
  private isAlertActive(
    alert: Pick<Alert, 'enabled' | 'startDate' | 'endDate'>,
  ): boolean {
    const startDate = DateEx.parseDateOnly(alert.startDate);
    const endDate = DateEx.parseDateOnly(alert.endDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return !(
      alert.enabled !== true ||
      (startDate != null && startDate > today) ||
      (endDate != null && endDate < today)
    );
  }
}

interface AlertDisplay {
  alertId: number;
  name: string;
  active: boolean;
  messages: number;
  newMessages: number;
}
